// Number questions drawn as a slider (htmlref: 'slider' in a community's
// schemaDefinition), e.g.
//   'Suggest people within': { type: 'Number', htmlref: 'slider',
//                               min: 1, max: 200, step: 1, unit: 'miles' }
// Everything comes from the schema. The top of the range means "that or
// further" — for a distance, any distance — and is shown and treated so.

export const isSlider = (cfg) => cfg?.htmlref === 'slider';

export const sliderRange = (cfg = {}) => ({
  min: Number.isFinite(Number(cfg.min)) ? Number(cfg.min) : 0,
  max: Number.isFinite(Number(cfg.max)) ? Number(cfg.max) : 100,
  step: Number(cfg.step) > 0 ? Number(cfg.step) : 1,
  unit: cfg.unit || '',
  curved: cfg.scale === 'curved',
});

// scale: 'curved' — small values get more of the track (value grows with
// the square of the handle position), snapped to tidy numbers: every 1 up
// to 10, every 5 up to 50, then every 10. The <input type="range"> then runs
// 0–1000 (position) and these convert to and from the real value.
export const POSITIONS = 1000;
const tidy = (n, { min, max, step }) => {
  const s = n < 10 ? step : n < 50 ? Math.max(step, 5) : Math.max(step, 10);
  return Math.min(max, Math.max(min, Math.round(n / s) * s));
};
export const valueAtPosition = (pos, cfg) => {
  const r = sliderRange(cfg);
  if (!r.curved) return Number(pos);
  const t = Math.min(1, Math.max(0, Number(pos) / POSITIONS));
  return tidy(r.min + (r.max - r.min) * t * t, r);
};
export const positionOfValue = (value, cfg) => {
  const r = sliderRange(cfg);
  const n = sliderNumber(value, cfg);
  if (!r.curved) return n;
  if (n == null) return null;
  const t = Math.sqrt(Math.max(0, Math.min(1, (n - r.min) / (r.max - r.min))));
  return Math.round(t * POSITIONS);
};
// The <input type="range"> attributes for a slider question.
export const rangeInputProps = (cfg) => {
  const r = sliderRange(cfg);
  return r.curved ? { min: 0, max: POSITIONS, step: 1 } : { min: r.min, max: r.max, step: r.step };
};

// Old text answers ("25 miles", "Anywhere in the UK") still read as numbers.
export const sliderNumber = (value, cfg) => {
  if (value === '' || value == null) return null;
  if (typeof value === 'number') return value;
  if (/^\s*\d+(\.\d+)?\s*$/.test(String(value))) return Number(value);
  const { max } = sliderRange(cfg);
  if (/any|anywhere|no limit/i.test(String(value))) return max;
  const n = Number((String(value).match(/\d+(\.\d+)?/) || [])[0]);
  return Number.isFinite(n) ? n : null;
};

export const isTopOfRange = (value, cfg) => {
  const n = sliderNumber(value, cfg);
  return n != null && n >= sliderRange(cfg).max;
};

// "25 miles", or at the top "200+ miles · any distance".
export const sliderLabel = (value, cfg) => {
  const n = sliderNumber(value, cfg);
  if (n == null) return 'Not set';
  const { max, unit } = sliderRange(cfg);
  // "1 mile", not "1 miles".
  const u = unit ? ` ${n === 1 ? unit.replace(/s$/, '') : unit}` : '';
  if (n >= max) return /mile|km|kilomet/i.test(unit) ? `${max}+${u} · any distance` : `${max}+${u}`;
  return `${n}${u}`;
};
